import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import {
    useGetAllTransaccs,
    useCreateTransacc,
    useUpdateTransacc
} from '@/shared/api/client/transacc-resource';
import { useGetLeafNodesWithGlProductAcFs } from '@/shared/api/client/coa-resource';
import {BigDecimalFilter, GetAllTransaccsParams, TransaccDTO} from '@/shared/api/model';
import createParamsSerializer from "@/shared/utils/create-params-serializer";

export const useTransaccs = () => {
    const queryClient = useQueryClient();
    const [error, setError] = useState<string | null>(null);
    const [acctypeFilter, setAcctypeFilter] = useState<BigDecimalFilter | undefined>(undefined);

    // Create transacc mutation
    const createTransaccMutation = useCreateTransacc({
        mutation: {
            onSuccess: () => {
                message.success('Account created successfully');
                queryClient.invalidateQueries({ queryKey: ['/api/transaccs'] });
            },
            onError: (error: any) => {
                console.error('Error creating account:', error);
                message.error('Failed to create account');
                setError('Failed to create account');
            }
        }
    });

    // Update transacc mutation
    const updateTransaccMutation = useUpdateTransacc({
        mutation: {
            onSuccess: () => {
                message.success('Account updated successfully');
                queryClient.invalidateQueries({ queryKey: ['/api/transaccs'] });
            },
            onError: (error) => {
                console.error('Error updating account:', error);
                message.error('Failed to update account');
                setError('Failed to update account');
            }
        }
    });

    // Get all transaccs with filters - no need to transform params anymore
    const params: GetAllTransaccsParams = {
        criteria: {},
        pageable: { page: 0, size: 100 }
    };

    if (acctypeFilter !== undefined) {
        params.criteria = { ...params.criteria, acctype: acctypeFilter };
    }

    const transaccsQuery = useGetAllTransaccs(params);

    // Get COA leaf nodes for GL account selection
    const coaLeafNodesQuery = useGetLeafNodesWithGlProductAcFs();

    // Create a new transacc
    const createTransacc = (transacc: TransaccDTO) => {
        createTransaccMutation.mutate({
            data: transacc
        });
    };

    // Update an existing transacc
    const updateTransacc = (transacc: TransaccDTO) => {
        updateTransaccMutation.mutate({
            data: transacc
        });
    };

    // Filter by account type
    const filterByAcctype = (acctype: BigDecimalFilter | undefined) => {
        setAcctypeFilter(acctype);
    };

    // Reset filters
    const resetFilters = () => {
        setAcctypeFilter(undefined);
    };

    // Refresh accounts data
    const loadTransaccs = () => {
        queryClient.invalidateQueries({ queryKey: ['/api/transaccs'] });
    };

    // Get account type label
    const getAcctypeLabel = (acctype: number): string => {
        switch (acctype) {
            case 0: return 'Cash';
            case 1: return 'Bank';
            case 2: return 'Transfer';
            default: return 'Unknown';
        }
    };

    return {
        // Data
        transaccs: transaccsQuery.data || [],
        coaLeafNodes: coaLeafNodesQuery.data || [],
        acctypeFilter,
        error,
        isLoading: transaccsQuery.isLoading || coaLeafNodesQuery.isLoading,
        isCreating: createTransaccMutation.isPending,
        isUpdating: updateTransaccMutation.isPending,

        // Actions
        createTransacc,
        updateTransacc,
        loadTransaccs,
        filterByAcctype,
        resetFilters,
        getAcctypeLabel
    };
};